import { Button, Heading, LuIcon, Separator, Text, toast } from "@overmap-ai/blocks"
import { OrganizationAccessLevel } from "@overmap-ai/common"
import { selectEmailDomainsOfOrganization, selectOrganizationById } from "@overmap-ai/core"
import { getRouteApi } from "@tanstack/react-router"
import { memo, useCallback, useMemo, useState } from "react"

import { ViewContainer } from "../../components/Containers/ViewContainer"
import { EmailVerificationModal } from "../../components/Dialogs/EmailVerificationModal"
import { OrganizationAccessRequired } from "../../components/OrganizationAccessRequired/AccessRequired"
import { useEmailDomainTable, useEmailDomainTableColumns } from "../../components/Tables"
import { Table } from "../../components/Tables/Table"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useAppSelector } from "../../store"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/settings/organization/email-domains")

export const OrganizationSettingsEmailDomainsView = memo(() => {
	const { organizationId } = routeApi.useParams()

	const organization = useAppSelector(selectOrganizationById(organizationId))

	if (!organization) {
		throw new Error(`Expected organization to be defined, but got ${organization}`)
	}

	const emailDomains = useAppSelector(selectEmailDomainsOfOrganization(organization.uuid))
	const [emailDomain, setEmailDomain] = useState("None")
	const [email, setEmail] = useState("")
	const [error, setError] = useState("")

	const handleEmailChange = useCallback((newEmail: string) => {
		let newEmailDomain = "None"
		if (newEmail.includes("@") && newEmail.indexOf("@") !== newEmail.length - 1) {
			newEmailDomain = newEmail.substring(newEmail.indexOf("@") + 1)
		}
		setEmail(newEmail)
		setEmailDomain(newEmailDomain)
	}, [])

	const addEmailDomain = useCallback(() => sdk.emailDomains.add(organization.uuid, email), [organization.uuid, email])

	const domainsToFilterOut = useMemo(
		() =>
			emailDomains.map((emailDomain) => {
				const domain = emailDomain.domain
				const indexOfAt = email.indexOf("@")
				const emailWithoutDomain = email.substring(0, indexOfAt > 0 ? indexOfAt : email.length)
				return `${emailWithoutDomain}@${domain}`
			}),
		[email, emailDomains],
	)

	const onSuccess = useCallback(
		() =>
			toast.success({
				title: "Verification email sent",
				description: `A verification email was sent to ${email} to confirm that you own the domain ${emailDomain}.`,
			}),
		[email, emailDomain],
	)

	const columns = useEmailDomainTableColumns()

	const table = useEmailDomainTable({
		data: emailDomains,
		columns,
	})

	return (
		<ViewContainer maxWidth="4xl">
			<section className="flex size-full flex-col gap-4 overflow-hidden">
				<div className="flex w-full flex-col gap-1">
					<Heading size="xl">{organization.name} email domains</Heading>
					<Text size="sm" accentColor="base">
						Manage organization email domains. Anyone with an email address at these domains will be
						automatically added to this organization.
					</Text>
				</div>

				<Separator size="full" />

				<div className="flex min-h-0 w-full grow flex-col gap-2">
					<div className="flex h-max w-full items-center justify-between">
						<Text size="sm" accentColor="base">
							{emailDomains.length} domains(s)
						</Text>
						<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.ADMIN} type="hide">
							<EmailVerificationModal
								email={email}
								onEmailChange={handleEmailChange}
								error={error}
								onErrorChange={setError}
								onConfirm={addEmailDomain}
								onSuccess={onSuccess}
								emailsToFilterOut={domainsToFilterOut}
								emailToFilterOutErrorMessage="This domain is already registered to this organization."
								title="Add email domain to this organization"
								description={
									"A verification email will be sent to the email address below to confirm that " +
									` you own the email domain ${emailDomain}`
								}
							>
								<Button size="sm" variant="surface">
									<LuIcon icon={modelIconMapping.add} />
									Create
								</Button>
							</EmailVerificationModal>
						</OrganizationAccessRequired>
					</div>

					<div className="min-h-0 w-full grow overflow-auto">
						<Table
							size="sm"
							variant="surface"
							className="w-full"
							table={{ ...table }}
							interactive={false}
							emptyText="No email domains."
						/>
					</div>
				</div>
			</section>
		</ViewContainer>
	)
})
OrganizationSettingsEmailDomainsView.displayName = "OrganizationSettingsEmailDomainsView"
